// URLSearchParams stringifies undefined/null as the literal text "undefined"/"null"
// if you pass them straight through — silently corrupting query params like
// doctor_id/status that are legitimately omitted. Strip those (and empty
// strings) before building the query string.
function buildQueryString(params = {}) {
  const filtered = Object.entries(params).filter(
    ([, value]) => value !== undefined && value !== null && value !== '',
  );
  return new URLSearchParams(filtered).toString();
}

export class AppointmentAPI {
  constructor(apiClient) {
    this.apiClient = apiClient;
  }

  async searchPatientForAppointment(q, limit = 10) {
    const queryString = buildQueryString({ q, limit });
    return this.apiClient.get(`/api/v1/opd/appointment/search-patient?${queryString}`);
  }

  async createAppointment(data) {
    return this.apiClient.post('/api/v1/opd/appointment/create', data);
  }

  async checkGuestMobile(mobile) {
    const queryString = buildQueryString({ mobile });
    return this.apiClient.get(`/api/v1/opd/appointment/check-guest-mobile?${queryString}`);
  }

  async getAppointmentById(id) {
    return this.apiClient.get(`/api/v1/opd/appointment/${id}`);
  }

  async listAppointments(params = {}) {
    const queryString = buildQueryString(params);
    return this.apiClient.get(`/api/v1/opd/appointment/list${queryString ? `?${queryString}` : ''}`);
  }

  async adminListAppointments(params = {}) {
    const queryString = buildQueryString(params);
    return this.apiClient.get(`/api/v1/opd/appointment/admin/list${queryString ? `?${queryString}` : ''}`);
  }

  async getCalendar(params = {}) {
    const queryString = buildQueryString(params);
    return this.apiClient.get(`/api/v1/opd/appointment/calendar${queryString ? `?${queryString}` : ''}`);
  }

  // Dashboard widget — appointment counts (total + per-status) for a date
  // window, optionally scoped to one doctor.
  async getDashboardStats({ date_from, date_to, doctor_id }) {
    const queryString = buildQueryString({ date_from, date_to, doctor_id });
    return this.apiClient.get(`/api/v1/opd/appointment/dashboard/stats?${queryString}`);
  }

  async checkIn(id, data) {
    return this.apiClient.put(`/api/v1/opd/appointment/${id}/check-in`, data);
  }

  async attend(id, data) {
    return this.apiClient.put(`/api/v1/opd/appointment/${id}/attend`, data);
  }

  // SCHEDULED -> ATTENDED in one go (e.g. dragging a Kanban card straight to
  // the Attended column) — backend still does a real check-in first.
  async attendDirect(id, data) {
    return this.apiClient.put(`/api/v1/opd/appointment/${id}/attend-direct`, data);
  }

  async complete(id, data) {
    return this.apiClient.put(`/api/v1/opd/appointment/${id}/complete`, data);
  }

  async cancel(id, data) {
    return this.apiClient.put(`/api/v1/opd/appointment/${id}/cancel`, data);
  }

  async reschedule(id, data) {
    return this.apiClient.put(`/api/v1/opd/appointment/${id}/reschedule`, data);
  }

  async getPossibleMatches(id) {
    return this.apiClient.get(`/api/v1/opd/appointment/${id}/possible-matches`);
  }

  async mapPatient(id, data) {
    return this.apiClient.put(`/api/v1/opd/appointment/${id}/map-patient`, data);
  }

  async reorderSerial(doctorId, date, data) {
    return this.apiClient.put(
      `/api/v1/opd/appointment/doctor/${doctorId}/date/${date}/reorder-serial`,
      data,
    );
  }

  async createReAppointment(id, data) {
    return this.apiClient.post(`/api/v1/opd/appointment/${id}/re-appointment`, data);
  }

  async createFollowupFromAppointment(id, data) {
    return this.apiClient.post(`/api/v1/opd/appointment/${id}/create-followup`, data);
  }

  async createPrescriptionFromAppointment(id, data) {
    return this.apiClient.post(`/api/v1/opd/appointment/${id}/create-prescription`, data);
  }

  async manualCronTrigger() {
    return this.apiClient.post('/api/v1/opd/appointment/cron/manual-trigger');
  }

  async getCronStats() {
    return this.apiClient.get('/api/v1/opd/appointment/cron/stats');
  }
}
